import { createSelector } from 'reselect';
import get from 'lodash/get';
import { takeEvery, take, put, select, call } from 'redux-saga/effects';
import { createConsts, createActions } from '../../utils/redux';
// send invitation
// accept / decline invitation
import { consts as acceptInviteToFamilyCircleConsts } from '../circles/acceptInviteToFamilyCircle';
import { consts as declineInviteToFamilyCircleConsts } from '../circles/declineInviteToFamilyCircle';
import { consts as acceptCaregiverRoleConsts } from '../circles/acceptCaregiverRole';
import { consts as caregiverConsts } from '../circles/caregiver';
import { consts as declineCaregiverRoleConsts } from '../circles/declineCaregiverRole';
import { consts as acceptGuardianRoleConsts } from '../circles/accept-guardian-role';
import { consts as declineGuardianRoleConsts } from '../circles/decline-guardian-role';
// remove from / leave circle
import { consts as myFamilyCircleConsts } from '../circles/myFamilyCircle';
// send / answer / completion survey
import { consts as sendSurveyConsts } from '../surveys/sendSurvey';
import { consts as answerSurveyConsts } from '../surveys/submit';
import { consts as submitReferralFormConsts } from '../surveys/submitReferralForm';
import { consts as upcomingSurveyCompletionsConsts } from '../surveys/upcoming-survey-completions';
// edit profile
import { consts as editUserConsts } from '../profiles/edit-user';
import { consts as editProviderConsts } from '../profiles/edit-provider';
import { consts as editPatientConsts } from '../profiles/edit-patient';
import { consts as reqAddPatientByProviderConsts } from '../profiles/addPatientByProvider';
import { consts as editPatientByProviderConsts } from '../profiles/edit-patient-by-provider';
import { consts as confirmMobilePhoneConsts } from '../profiles/confirm-mobile-phone';
import { consts as changeSettingsConsts } from '../profiles/change-settings';
import { consts as reqAssignPatientConsts } from '../board/reqAssignPatient';
import { consts as inviteCaregiverFamilyConsts } from '../circles/inviteCaregiverFamily';
import { consts as assignProgramsConsts } from '../programs/assignPrograms';
import { consts as confirmVisitConsts } from '../appointmentForm/requests/confirm-visit-GW-req';
import { consts as confirmVisitSelfSchedulingConsts } from '../appointmentForm/requests/confirm-visit-selfScheduling-req';
import { consts as shareToChatConsts } from '../healthboard/patientPortal/share-to-chat-req';
import { consts as shareToEmailConsts } from '../healthboard/patientPortal/share-to-email-req';
import chatConsts from '../chatConnection/constants';
import { showMobileAppPopup } from './popup';
import { signupActions } from '../auth/signup/actions';

import { consts as removeAssignedProviderConsts } from '../circles/removeAssignedProvider';
import { consts as reqAssignProviderConsts } from '../board/reqAssignProvider';

import checkIsAppStoreLinksAvailability from '../../utils/checkIsAppStoreLinksAvailability';

import {
  SUCCESS_INVITE_CAREGIVER,
  ACCEPT_FAMILY_INVITATION,
  DECLINE_FAMILY_INVITATION,
  ACCEPT_CAREGIVER_INVITATION,
  DECLINE_CAREGIVER_INVITATION,
  SUCCESS_REMOVE_FAMILY_MEMBER,
  SUCCESS_SEND_SURVEY,
  SUCCESS_SURVEY_COMPLETION,
  SUCCESS_ANSWER_SURVEY,
  SUCCESS_ANSWER_FORM,
  SUCCESS_UPDATE_PROFILE,
  SUCCESS_UPDATE_SETTINGS,
  SUCCESS_JOIN_CIRCLE,
  REMOVE_FROM_CHAT,
  PROGRAMS,
  SUCCESS_PATIENT_ADDED,
  SUCCESS_SENT_DOCUMENT_TO_EMAIL,
  SUCCESS_UNASSIGNED,
  SUCCESS_ASSIGNED,
  SUCCESS_PAYMENT_METHOD_REMOVED,
} from '../../constants/strings';
import { APPOINTMENT_DETAILS_TYPES } from '../healthboard/appointment-details';
import {
  isLoggedInSelector,
  selectSuccessAuthAzureForSignUpFlow,
} from '../auth/selectors';
import { APPOINTMENT_STATUSES } from '../../constants';
import {
  PAPERWORK,
  APPOINTMENT_FORM,
  DEPENDENT_FORM,
  HEALTHBOARD,
} from '../../constants/routes';

// payment methods (delete card)
import { consts as deleteCardConsts } from '../payment-methods/requests/delete-card-req';

const EDIT_USER_HIDDEN_PATHS = [
  PAPERWORK,
  APPOINTMENT_FORM,
  DEPENDENT_FORM,
  HEALTHBOARD,
];

function shouldRenderSuccessMessage(pathname) {
  return !EDIT_USER_HIDDEN_PATHS.some(hidden => pathname.includes(hidden));
}

//= =================PROFILE_ACTIONS==================

const EDIT_PROFILE_ACTIONS = [
  editUserConsts.SUCCEED,
  editProviderConsts.SUCCEED,
  editPatientConsts.SUCCEED,
  editPatientByProviderConsts.SUCCEED,
  confirmMobilePhoneConsts.SUCCEED,
];

const PROFILE_ACTIONS = [
  changeSettingsConsts.SUCCEED,
  reqAssignPatientConsts.SUCCEED,
  inviteCaregiverFamilyConsts.SUCCEED,
  assignProgramsConsts.SUCCEED,
  reqAddPatientByProviderConsts.MESSAGE,
  editUserConsts.SEND,
  chatConsts.REMOVE_CHANNEL_MESSAGE,
  removeAssignedProviderConsts.SUCCEED,
  reqAssignProviderConsts.SUCCEED,
];

const getProfileSuccessMsg = actionType => {
  if (actionType === removeAssignedProviderConsts.SUCCEED) {
    return SUCCESS_UNASSIGNED;
  }

  if (actionType === reqAssignProviderConsts.SUCCEED) {
    return SUCCESS_ASSIGNED;
  }

  if (actionType === changeSettingsConsts.SUCCEED) {
    return SUCCESS_UPDATE_SETTINGS;
  }

  if (actionType === inviteCaregiverFamilyConsts.SUCCEED) {
    return SUCCESS_INVITE_CAREGIVER;
  }

  if (actionType === reqAssignPatientConsts.SUCCEED) {
    return SUCCESS_JOIN_CIRCLE;
  }

  if (EDIT_PROFILE_ACTIONS.includes(actionType)) {
    const { pathname } = window.location;

    if (!shouldRenderSuccessMessage(pathname)) {
      return null;
    }

    return SUCCESS_UPDATE_PROFILE;
  }

  if (actionType === reqAddPatientByProviderConsts.MESSAGE) {
    return SUCCESS_PATIENT_ADDED;
  }

  if (actionType === chatConsts.REMOVE_CHANNEL_MESSAGE) {
    return REMOVE_FROM_CHAT;
  }

  if (actionType === assignProgramsConsts.SUCCEED) {
    return PROGRAMS.invitations.success;
  }

  return null;
};

//= ================================================
//= =================SURVEY_ACTIONS==================

const SURVEY_ACTIONS = [
  sendSurveyConsts.SUCCEED,
  upcomingSurveyCompletionsConsts.MESSAGE,
  answerSurveyConsts.MESSAGE,
  submitReferralFormConsts.MESSAGE,
];

const getSurveySuccessMsg = (actionType, message) => {
  if (actionType === sendSurveyConsts.SUCCEED) {
    return SUCCESS_SEND_SURVEY;
  }

  if (actionType === answerSurveyConsts.MESSAGE) {
    return message || SUCCESS_ANSWER_SURVEY;
  }

  if (actionType === submitReferralFormConsts.MESSAGE) {
    return message || SUCCESS_ANSWER_FORM;
  }

  if (actionType === upcomingSurveyCompletionsConsts.MESSAGE) {
    return message || SUCCESS_SURVEY_COMPLETION;
  }

  return null;
};

//= ================================================

const supportedActions = ['UPDATE', 'ASSIGN_CAREGIVER_ROLE', 'SIGN_UP'];

export const consts = createConsts('ui', 'success', ...supportedActions);
export const actions = createActions(consts);

const initialState = {
  message: null,
  acceptCaregiverRole: false, // in progress flag
  signUp: false, // in progress flag
  values: null,
};

// reducer
export default (state = initialState, action) => {
  // Accept caregiver role handler
  // Signup (password & T&C setting) handler
  if (
    (state.acceptCaregiverRole || state.signUp) &&
    action.type === consts.UPDATE
  ) {
    return state;
  }
  const { iconType = 'success', values } = action;

  switch (action.type) {
    case consts.UPDATE:
      return {
        ...state,
        values,
        message: action.payload,
        iconType,
        title: get(action, 'payload.title', null),
      };
    case consts.ASSIGN_CAREGIVER_ROLE:
      return {
        ...state,
        acceptCaregiverRole: action.payload,
      };
    case consts.SIGN_UP:
      return {
        ...state,
        signUp: action.payload,
      };
    default:
      return state;
  }
};

// selectors
export const successSelector = state => get(state, 'ui.success');
export const successMsgSelector = createSelector(
  successSelector,
  state => get(state, 'message', null),
);

export const successValuesForMsgSelector = createSelector(
  successSelector,
  state => get(state, 'values', {}),
);

export const succesTypeSelector = createSelector(
  successSelector,
  state => get(state, 'iconType', null),
);

export const successAlertBoxTitleSelector = createSelector(
  successSelector,
  state => get(state, 'title', null),
);

const getSuccessMsg = (actionType, message) => {
  // accept / decline invitation
  if (actionType === acceptInviteToFamilyCircleConsts.SUCCEED) {
    return ACCEPT_FAMILY_INVITATION;
  }

  if (actionType === declineInviteToFamilyCircleConsts.SUCCEED) {
    return DECLINE_FAMILY_INVITATION;
  }

  if (actionType === acceptCaregiverRoleConsts.SUCCEED) {
    return ACCEPT_CAREGIVER_INVITATION;
  }

  if (actionType === declineCaregiverRoleConsts.SUCCEED) {
    return DECLINE_CAREGIVER_INVITATION;
  }

  if (actionType === acceptGuardianRoleConsts.SUCCEED) {
    return {
      id: 'circles.invites.guardian.accept',
    };
  }

  if (actionType === declineGuardianRoleConsts.SUCCEED) {
    return {
      id: 'circles.invites.guardian.decline',
    };
  }

  if (actionType === myFamilyCircleConsts.REMOVE_ITEM) {
    return SUCCESS_REMOVE_FAMILY_MEMBER;
  }

  if (actionType === confirmVisitSelfSchedulingConsts.SUCCEED) {
    return {
      id:
        message.status.coreAppName === APPOINTMENT_STATUSES.PENDING
          ? 'appointments.successSchedulePendingMsg'
          : 'appointments.successScheduleMsg',
    };
  }

  if (actionType === confirmVisitConsts.SUCCEED) {
    return {
      id: 'appointments.successScheduleMsg',
    };
  }

  if (actionType === shareToChatConsts.SUCCEED) {
    return {
      id: 'patient.portal.healthRecord.shareToChatSuccess',
    };
  }

  if (actionType === APPOINTMENT_DETAILS_TYPES.PAYMENT_SUCCESS) {
    return {
      id: 'appointment.payment.charged',
      title: 'appointment.payment.submitted',
    };
  }

  if (actionType === APPOINTMENT_DETAILS_TYPES.VISIT_CONFIRMED) {
    return {
      id: 'appointment.visitConfirmed',
    };
  }

  if (actionType === shareToEmailConsts.SUCCEED) {
    return SUCCESS_SENT_DOCUMENT_TO_EMAIL;
  }

  if (actionType === deleteCardConsts.SUCCEED) {
    return SUCCESS_PAYMENT_METHOD_REMOVED;
  }

  //= =================SURVEY_ACTIONS==================
  const surveySuccessMsg = getSurveySuccessMsg(actionType, message);
  if (surveySuccessMsg) return surveySuccessMsg;

  //= =================PROFILE_ACTIONS==================
  const profileSuccessMsg = getProfileSuccessMsg(actionType);
  if (profileSuccessMsg) return profileSuccessMsg;

  return null;
};

// saga
function* updateSuccessMsgWorker(action) {
  if (action.type === acceptCaregiverRoleConsts.SUCCEED) {
    // Avoid incorrect message displaying, as on accepting caregiver role -
    // user no longer sees target user in the list of family circles [he belongs to]
    // but he sees target user in the 'You are caregiver' list
    yield put({ type: consts.ASSIGN_CAREGIVER_ROLE, payload: true });
    yield take(caregiverConsts.SUCCEED);
    yield put({ type: consts.ASSIGN_CAREGIVER_ROLE, payload: false });
  }

  const isLoggedIn = yield select(isLoggedInSelector);
  const isSuccessAzureSignUp = yield select(
    selectSuccessAuthAzureForSignUpFlow,
  );
  const isEditUserAction = action.type === editUserConsts.SEND;
  const isRegistrationProcess =
    (isEditUserAction && get(action, 'payload.password')) ||
    (isSuccessAzureSignUp && isEditUserAction && !isLoggedIn);

  if (isRegistrationProcess) {
    // Avoid message displaying on signup (password & T&C setting)
    yield put({ type: consts.SIGN_UP, payload: true });
    yield take(editUserConsts.SUCCEED);

    const { hasAtLeastOneStoreLink } = yield call(
      checkIsAppStoreLinksAvailability,
    );

    if (hasAtLeastOneStoreLink) {
      yield put(showMobileAppPopup());
    }

    yield put(signupActions.reset());
    yield put({ type: consts.SIGN_UP, payload: false });
    return;
  }

  let iconType;
  if (action.type === chatConsts.REMOVE_CHANNEL_MESSAGE) {
    iconType = 'deleted-chat';
  }

  if (action.type === confirmVisitSelfSchedulingConsts.SUCCEED) {
    iconType =
      action.payload.status.coreAppName === APPOINTMENT_STATUSES.PENDING
        ? 'ico-48-clock'
        : 'success';
  }

  if (
    [shareToChatConsts.SUCCEED, shareToEmailConsts.SUCCEED].includes(
      action.type,
    )
  ) {
    iconType = 'icon-48-share';
  }

  yield put({
    type: consts.UPDATE,
    payload: getSuccessMsg(action.type, action.payload),
    values: action.values,
    iconType,
  });
}

export function* updateSuccessMsgSaga() {
  yield takeEvery(
    [
      acceptInviteToFamilyCircleConsts.SUCCEED,
      declineInviteToFamilyCircleConsts.SUCCEED,
      acceptCaregiverRoleConsts.SUCCEED,
      declineCaregiverRoleConsts.SUCCEED,
      acceptGuardianRoleConsts.SUCCEED,
      declineGuardianRoleConsts.SUCCEED,
      myFamilyCircleConsts.REMOVE_ITEM,
      confirmVisitSelfSchedulingConsts.SUCCEED,
      confirmVisitConsts.SUCCEED,
      shareToChatConsts.SUCCEED,
      shareToEmailConsts.SUCCEED,
      APPOINTMENT_DETAILS_TYPES.VISIT_CONFIRMED,
      deleteCardConsts.SUCCEED,
      ...SURVEY_ACTIONS,
      ...PROFILE_ACTIONS,
      ...EDIT_PROFILE_ACTIONS,
    ],
    updateSuccessMsgWorker,
  );
}



// WEBPACK FOOTER //
// ./src/redux/ui/success.js